<template>
  <div
    class="search-home"
    :class="{
      'mid-autumn-theme': isMidAutumn,
      'national-day-theme': isNationalDay,
    }"
    :style="backgroundStyle"
  >
    <header class="topbar">
      <!-- 主要导航 -->
      <nav class="primary-nav" :aria-label="$t('devUpdates.mainNavigation')">
        <a
          v-for="(item, index) in visibleNavList"
          :key="`${item.url || item.title}-${index}`"
          @click="handleNavClick(item)"
        >{{ displayNavTitle(item) }}</a>
      </nav>
      <div class="top-actions">
        <GeneralAgentServiceTrigger />
        <LocaleSwitcher compact />
        <!-- 登录 -->
        <button
          v-if="!isLoggedIn"
          class="login"
          type="button"
          @click="openLogin"
        >
          {{ $t("common.login") }}
        </button>
        <button
          v-else
          class="user-entry"
          type="button"
          :title="devSyncText(displayName)"
          @click="go('/pc/user')"
        >
          <!-- 用户头像 -->
          <img :src="avatarUrl" :alt="$t('account.avatar')" /><span>{{
            displayName
          }}</span>
        </button>
      </div>
    </header>

    <main class="search-stage">
      <div class="search-group">
        <!-- 先搜 -->
        <img
          class="brand-logo"
          :src="logoImage"
          :alt="$t('priceChannel.businessDisplay.subject.XSOU')"
        />
        <!-- 庆祝中华人民共和国成立 -->
        <!-- 周年 -->
        <div v-if="isNationalDay" class="anniversary-badge">
          {{ $t("devUpdates.celebratingTheFoundingOfThePeoplesRepublicOfChina")
          }}{{ nationalDayAnniversary }}{{ $t("devUpdates.anniversary") }}
        </div>
        <form class="search-form" role="search" @submit.prevent="search">
          <svg class="search-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M9.5 3a6.5 6.5 0 1 0 3.98 11.64L19.85 21 21 19.85l-6.36-6.37A6.5 6.5 0 0 0 9.5 3Zm0 1.65a4.85 4.85 0 1 1 0 9.7 4.85 4.85 0 0 1 0-9.7Z"
            />
          </svg>
          <!-- 搜索料号、名称 -->
          <input
            ref="input"
            v-model="keyword"
            type="search"
            :placeholder="$t('common.searchPlaceholder')"
            :aria-label="$t('common.searchPlaceholder')"
            autocomplete="off"
          />
          <!-- 清除 -->
          <button
            v-if="keyword"
            class="clear"
            type="button"
            :aria-label="$t('mobile.home.clear')"
            @click="clearSearch"
          >
            ×
          </button>
          <div class="search-actions">
            <!-- 先搜一下 -->
            <button class="search-button" type="submit">
              {{ $t("common.searchAction") }}
            </button>
            <!-- 先搜AI -->
            <button class="ai-search-button" type="button" @click="aiSearch">
              {{ $t("pcHome.ai") }}
            </button>
          </div>
        </form>
        <!-- 快捷型号搜索 -->
        <div class="quick-models" :aria-label="$t('devUpdates.quickPartNumberSearch')">
          <!-- 快捷搜索 -->
          <span>{{ $t("devUpdates.quickSearch") }}</span>
          <button
            v-for="model in quickModels"
            :key="model"
            type="button"
            @click="quickSearch(model)"
          >
            {{ model }}
          </button>
        </div>
      </div>
    </main>

    <footer>
      <!-- 联系我们 -->
      <!-- 加入我们 -->
      <!-- 隐私说明 -->
      <!-- 帮助中心 -->
      <nav class="footer-info-links">
        <!-- 关于先搜 -->
        <a @click="goAbout('about_xsou')">{{ $t("pcAbout.about") }}</a
        ><a @click="goAbout('contact')">{{ $t("pcAbout.contact") }}</a
        ><a @click="goAbout('join')">{{ $t("pcAbout.join") }}</a
        ><a @click="goAbout('privacy')">{{ $t("pcAbout.privacy") }}</a
        ><a @click="goAbout('help')">{{ $t("pcAbout.help") }}</a>
      </nav>
      <div class="footer-copyright">
        <!-- Copyright © 2017-2026 先搜科技 ( -->
        <span>{{ $t("devUpdates.copyright20172026XsouTechnology") }}</span
        ><a
          href="https://www.xsou.com"
          target="_blank"
          rel="noopener noreferrer"
          >https://www.xsou.com</a
        ><span>) All Rights Reserved.</span>
        <a
          href="https://beian.miit.gov.cn/"
          target="_blank"
          rel="noopener noreferrer"
          >粤ICP备18129742号</a
        >
      </div>
      <!-- XML工具 -->
      <nav class="footer-links">
        <!-- ASC工具 -->
        <a
          href="https://www.xsou.com/plugs/asc.html"
          target="_blank"
          rel="noopener noreferrer"
          >{{ $t("pcFooter.ascTool") }}</a
        ><a
          href="https://www.xsou.com/plugs/xml-tool.html"
          target="_blank"
          rel="noopener noreferrer"
          >{{ $t("pcFooter.xmlTool") }}</a
        >
      </nav>
    </footer>

    <transition name="festival-greeting">
      <div
        v-if="showFestivalGreeting"
        class="festival-greeting-mask"
        role="presentation"
        @click.self="closeFestivalGreeting"
      >
        <section
          class="festival-greeting-card"
          :class="`${activeDoodle.themeKey || activeDoodle.key}-greeting`"
          role="dialog"
          aria-modal="true"
          :aria-label="devSyncText(festivalGreetingTitle)"
        >
          <!-- 关闭节日祝福 -->
          <button
            class="festival-greeting-close"
            type="button"
            :aria-label="$t('devUpdates.closeHolidayGreeting')"
            @click="closeFestivalGreeting"
          >
            ×
          </button>
          <span class="greeting-spark spark-one">✦</span
          ><span class="greeting-spark spark-two">✦</span
          ><span class="greeting-spark spark-three">●</span>
          <div class="greeting-art">
            <!-- XSOU节日标志 -->
            <img
              :src="festivalGreetingImage"
              :alt="$t('devUpdates.xsouHolidayLogo')"
            />
          </div>
          <div class="greeting-copy">
            <p class="greeting-kicker">{{ festivalGreetingKicker }}</p>
            <h2>{{ devSyncText(festivalGreetingTitle) }}</h2>
            <h2 class="greeting-subtitle">{{ devSyncText(festivalGreetingSubtitle) }}</h2>
          </div>
          <div class="greeting-progress" aria-hidden="true"></div>
        </section>
      </div>
    </transition>
    <AuthDialog
      :visible.sync="loginDialogVisible"
      initial-mode="login"
      initial-login-mode="sms"
    />
  </div>
</template>

<script>
import devSyncI18n from '@/i18n/common/index';

import { getBannerAdv } from "@/api/home";
import { resolveHomeDoodle } from "@/indexlogo/schedule";
import AuthDialog from "@/views/pc/layout/components/AuthDialog.vue";
import LocaleSwitcher from "@/components/common/LocaleSwitcher.vue";
import { navigationMessageKey } from '@/i18n/catalog';
import { withPriceChannelHomeNavigation } from '@/services/price-channel/business-navigation';
import GeneralAgentServiceTrigger from "@/components/agent-service/GeneralAgentServiceTrigger.vue";

export default {
  mixins: [devSyncI18n],
  name: "GoogleStyleHomePage",
  components: { AuthDialog, LocaleSwitcher, GeneralAgentServiceTrigger },
  data() {
    return {
      // 首页搜索框内容及常用料号快捷入口。
      keyword: "",
      quickModels: ["PRM256M8V89CG8GQF-15E", "WDS", "0T25", "SUM512M8Z10BD9SA-PG"],
      // 顶部语言菜单的展开状态。
      // logoImage 会随节日主题变化，defaultLogoImage 保存接口返回的常规 Logo。
      logoImage: require("@/assets/pc/home-search-logo.png"),
      defaultLogoImage: require("@/assets/pc/home-search-logo.png"),
      // 首页主体区域的默认背景图。
      backgroundImage: require("@/assets/bg/home-map-snow-white.png"),
      // 当前命中的节日主题配置以及定时刷新任务。
      activeDoodle: null,
      doodleTimer: null,
      // 节日祝福弹层的关闭定时器、显示状态和本页去重标识。
      greetingTimer: null,
      showFestivalGreeting: false,
      greetedDoodleKey: "",
      // 首页登录弹窗状态。
      loginDialogVisible: false,
      // 与 PC 公共布局同源的顶部导航原始数据。
      navList: [],
    };
  },
  computed: {
    // 将背景资源转换成模板可直接绑定的行内样式。
    backgroundStyle() { return { backgroundImage: `url(${this.backgroundImage})` }; },
    // 节日类型决定首页配色、文案与祝福样式。
    isMidAutumn() { return this.activeDoodle && this.activeDoodle.themeKey === "mid-autumn"; },
    isNationalDay() { return this.activeDoodle && this.activeDoodle.themeKey === "national-day"; },
    nationalDayAnniversary() { return (this.activeDoodle && this.activeDoodle.anniversary) || 77; },
    // 弹层优先使用节日导航图，未配置时回退到常规 Logo。
    festivalGreetingImage() {
      if (this.activeDoodle && this.activeDoodle.navImage)
        return this.activeDoodle.navImage;
      return this.defaultLogoImage;
    },
    festivalGreetingKicker() {
      return this.isNationalDay
        ? `盛世华诞 · ${this.nationalDayAnniversary}周年`
        : "花好月圆 · 情满中秋";
    },
    festivalGreetingTitle() {
      return this.isNationalDay ? "祝祖国生日快乐！" : "祝您中秋快乐";
    },
    festivalGreetingSubtitle() {
      return this.isNationalDay ? "祝您国庆快乐！" : "阖家幸福！";
    },
    isLoggedIn() {
      return Boolean(this.$store.getters.token);
    },
    userProfile() {
      return {
        ...(this.$store.state.user.userInfo || {}),
        ...(this.$store.state.user.userDetails || {}),
      };
    },
    avatarUrl() {
      return this.userProfile.avatar || require("@/assets/default-avatar.png");
    },
    displayName() {
      return (
        this.userProfile.authnName ||
        this.userProfile.nick_name ||
        this.userProfile.nickName ||
        this.userProfile.mobile ||
        "个人中心"
      );
    },
    // 首页保留首页入口；行情固定紧接百科，不受后台旧入口位置影响。
    visibleNavList() {
      return withPriceChannelHomeNavigation(this.navList, item => this.normalizeNavUrl(item));
    },
  },
  watch: {
    // 支持通过 URL 参数预览指定节日主题。
    "$route.query.doodle"() { this.refreshDoodle(); this.$nextTick(this.openFestivalGreeting); },
    // 登录后重新请求导航，使“用户中心”等登录态菜单及时更新。
    isLoggedIn() { this.getNavList(); },
  },
  created() {
    // 页面创建时同步初始化节日主题和顶部导航。
    this.refreshDoodle();
    this.getNavList();
    // 获取后台配置的首页常规 Logo，接口异常时继续使用本地默认资源。
    getBannerAdv({ which: 1, type: 1, position: "HOME", status: 0 }).then((res) => {
      if (res.code !== 200 || !Array.isArray(res.data)) return;
      const assets = [...res.data].sort((a, b) => a.sort - b.sort);
      if (assets[0] && assets[0].image) this.defaultLogoImage = assets[0].image;
      this.refreshDoodle();
    }).catch(() => {});
  },
  mounted() {
    // 首次进入首页自动聚焦搜索框，并按需展示当天的节日祝福。
    this.$nextTick(() => { if (this.$refs.input) this.$refs.input.focus(); this.openFestivalGreeting(); });
    // 跨越节日生效时间时无需刷新页面即可切换主题。
    this.doodleTimer = window.setInterval(this.refreshDoodle, 10 * 1000);
  },
  // 组件销毁时清理全部首页定时器，避免后台任务继续运行。
  beforeDestroy() { window.clearInterval(this.doodleTimer); window.clearTimeout(this.greetingTimer); },
  methods: {
    displayNavTitle(item) {
      // 行情
      if (this.normalizeNavUrl(item).split(/[?#]/)[0] === '/pc/pricechannel/display') return this.$t('pcHome.marketNav');
      const key = navigationMessageKey(this.normalizeNavUrl(item), item?.title);
      // 动态简中示例：按 navigationMessageKey 的路由／标题映射取词；未知导航保留原名；完整中文见对应 zh-CN.js，示例不代表固定返回值
      // nav.home：首页
      // nav.publish：发布
      // nav.featured：精选
      // nav.bidding：竞标
      // nav.supply：供需
      // nav.wiki：百科
      return key ? this.$t(key) : this.devSyncText(item.title);
    },
    // 根据当前时间或 doodle 查询参数刷新首页节日主题。
    refreshDoodle() {
      const previousKey = this.activeDoodle && this.activeDoodle.key;
      const doodle = resolveHomeDoodle(
        new Date(),
        this.$route.query.doodle || "",
      );
      this.activeDoodle = doodle;
      this.logoImage = doodle ? doodle.image : this.defaultLogoImage;
      this.updateShareMetadata();
      if (this._isMounted && doodle && doodle.key !== previousKey)
        this.$nextTick(this.openFestivalGreeting);
    },
    // 将当前主题同步到 Open Graph、Twitter Card 等分享元数据。
    updateShareMetadata() {
      if (typeof document === "undefined" || typeof window === "undefined")
        return;
      const siteTitle = document.title.replace(/\s+/g, " ").trim();
      const description = this.activeDoodle
        ? `${this.festivalGreetingTitle}，${this.festivalGreetingSubtitle}`
        : "找芯片，上先搜。先搜科技祝您事业顺利、生活幸福！";
      const image = new URL(
        this.festivalGreetingImage || this.logoImage,
        window.location.origin,
      ).href;
      const pageUrl = `${window.location.origin}/`;
      const values = [
        ["property", "og:type", "website"],
        ["property", "og:site_name", "先搜科技"],
        ["property", "og:title", siteTitle],
        ["property", "og:description", description],
        ["property", "og:image", image],
        ["property", "og:url", pageUrl],
        ["name", "twitter:card", "summary_large_image"],
        ["name", "twitter:title", siteTitle],
        ["name", "twitter:description", description],
        ["name", "twitter:image", image],
        ["itemprop", "name", siteTitle],
        ["itemprop", "description", description],
        ["itemprop", "image", image],
      ];
      values.forEach(([attribute, key, content]) => {
        let meta = document.head.querySelector(`meta[${attribute}="${key}"]`);
        if (!meta) {
          meta = document.createElement("meta");
          meta.setAttribute(attribute, key);
          document.head.appendChild(meta);
        }
        meta.setAttribute("content", content);
      });
    },
    // 每个节日每天只展示一次祝福弹层，并在三秒后自动关闭。
    openFestivalGreeting() {
      // 暂停首页节日祝福弹窗；恢复时取消下方逻辑的注释。
      /*
      if (!this.activeDoodle || this.greetedDoodleKey === this.activeDoodle.key) return;
      const storageKey = this.festivalGreetingStorageKey(this.activeDoodle.key);
      try {
        if (window.localStorage.getItem(storageKey) === "1") {
          this.greetedDoodleKey = this.activeDoodle.key;
          return;
        }
        window.localStorage.setItem(storageKey, "1");
      } catch (error) {
        // 隐私模式禁用本地存储时仍正常展示，本次页面内依靠 greetedDoodleKey 去重。
      }
      window.clearTimeout(this.greetingTimer);
      this.greetedDoodleKey = this.activeDoodle.key;
      this.showFestivalGreeting = true;
      this.greetingTimer = window.setTimeout(this.closeFestivalGreeting, 3000);
      */
    },
    // 使用上海时区生成按“节日 + 日期”隔离的本地存储键。
    festivalGreetingStorageKey(doodleKey) {
      const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Shanghai",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date());
      const value = (type) =>
        parts.find((part) => part.type === type)?.value || "";
      return `xsou-festival-greeting:${doodleKey}:${value("year")}-${value("month")}-${value("day")}`;
    },
    // 手动或定时关闭节日祝福，并同步清理关闭任务。
    closeFestivalGreeting() {
      window.clearTimeout(this.greetingTimer);
      this.showFestivalGreeting = false;
    },
    // 将有效关键词带到 PC 搜索结果页。
    search() {
      const value = this.keyword.trim();
      if (value)
        this.$router.push({ path: "/pc/search", query: { keyword: value } });
    },
    quickSearch(model) {
      this.keyword = model;
      this.$nextTick(this.search);
    },
    // 快捷料号复用普通搜索流程，保证跳转行为一致。
    quickSearch(model) { this.keyword = model; this.$nextTick(this.search); },
    // 复用 PC 公共布局的导航接口、排序和登录态过滤规则。
    getNavList() {
      getBannerAdv({ which: 1, type: 5, position: "LAYOUT", status: 0 })
        .then((res) => {
          const { code, data = [] } = res || {};
          this.navList = [];
          if (code !== 200 || !Array.isArray(data) || !data.length) return;

          let processedList = [...data]
            .sort((a, b) => a.sort - b.sort)
            .filter((item) => item.status === 0);
          if (!this.isLoggedIn) {
            processedList = processedList.filter(
              (item) => item.url !== "/pc/user",
            );
          }
          processedList = processedList.filter((item) => {
            const title = String(item.title || "").trim();
            const path = String(this.normalizeNavUrl(item) || "")
              .split(/[?#]/)[0]
              .replace(/\/$/, "");
            return (
              title !== "知道" &&
              !["/pc/wiki/questions", "/pc/wiki/community"].includes(path)
            );
          });
          this.navList = processedList;
        })
        .catch((error) => {
          this.navList = [];
          console.error("请求顶部导航失败:", error);
        });
    },
    // 兼容后台历史导航地址，将同类入口统一到当前 PC 路由。
    normalizeNavUrl(item) {
      const title = String(item && item.title ? item.title : "").trim();
      const url = typeof item === "string" ? item : item && item.url;
      if (title === "知道") return "/pc/wiki/questions";
      if (
        title === "供需" ||
        title === "供求" ||
        ["/info", "/pc/info", "/pc/supply-demand/hall"].includes(url)
      ) {
        return "/pc/clue";
      }
      return url;
    },
    // 处理导航跳转；未登录访问受保护的发布入口时先打开登录框。
    handleNavClick(item) {
      const url = this.normalizeNavUrl(item);
      if (
        [
          "/source/publish",
          "/source/publish/fast",
          "/pc/source/publish",
          "/pc/source/publish/fast",
        ].includes(url) &&
        !this.isLoggedIn
      ) {
        this.openLogin();
        return;
      }
      if (url && this.$route.path !== url) this.$router.push(url);
    },
    // AI 搜索允许游客进入；游客额度由 AI 页面在第 4 次发送时统一拦截。
    aiSearch() {
      const question = this.keyword.trim();
      this.$router.push({ path: "/pc/myAi", query: question ? { question, intent: "site-search" } : {} });
    },
    // 清空搜索内容后恢复输入焦点。
    clearSearch() { this.keyword = ""; this.$nextTick(() => this.$refs.input && this.$refs.input.focus()); },
    // 首页内部的通用路由跳转封装。
    go(path) { this.$router.push(path); },
    // 打开“关于先搜”页面中的指定内容区块。
    goAbout(section) { this.$router.push({ path: "/pc/about", query: { section } }); },
    // 打开首页共用的登录/注册弹窗。
    openLogin() { this.loginDialogVisible = true; },
  },
};
</script>

<style lang="less" scoped>
.search-home {
  min-height: 100vh;
  display: grid;
  grid-template-rows: 64px 1fr 54px;
  overflow: hidden;
  background-color: #f7f9fc;
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  color: #202124;
  font-family: Arial, "PingFang SC", "Microsoft YaHei", sans-serif;
}
.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 22px;
  box-sizing: border-box;
  background: transparent;
}
.primary-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  a {
    padding: 9px 10px;
    color: #202124;
    font-size: 14px;
    cursor: pointer;
    &:hover {
      color: #4286f5;
    }
  }
}
.top-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.login {
  min-width: 76px;
  height: 40px;
  padding: 0 20px;
  border: 0;
  border-radius: 7px;
  background: #4286f5;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #3277e8;
  }
}
.user-entry {
  max-width: 172px;
  height: 34px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 5px;
  border: 0;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.86);
  color: #344054;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(44, 68, 104, 0.08);
  &:hover {
    background: #fff;
    box-shadow: 0 3px 14px rgba(44, 68, 104, 0.14);
  }
  img {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
    border-radius: 50%;
    object-fit: cover;
    border: 1px solid rgba(66, 134, 245, 0.22);
  }
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
  }
}
.search-stage {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 24px 12vh;
  box-sizing: border-box;
}
.search-group {
  position: relative;
  width: min(720px, calc(100vw - 48px));
  display: flex;
  flex-direction: column;
  align-items: center;
}
.brand-logo {
  display: block;
  width: 85%;
  height: auto;
  max-height: none;
  margin-bottom: 36px;
  object-fit: contain;
}
.anniversary-badge {
  margin: -23px 0 18px;
  padding: 7px 22px;
  border: 1px solid rgba(215, 35, 35, 0.26);
  border-radius: 18px;
  background: linear-gradient(
    90deg,
    rgba(255, 248, 220, 0.94),
    rgba(255, 255, 255, 0.98),
    rgba(255, 248, 220, 0.94)
  );
  color: #c51920;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 2px;
  box-shadow: 0 4px 14px rgba(181, 39, 31, 0.12);
  white-space: nowrap;
}
.search-form {
  position: relative;
  width: 100%;
  height: 58px;
  display: flex;
  align-items: center;
  padding: 5px 6px 5px 20px;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 30px;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 7px 24px rgba(44, 68, 104, 0.14);
  backdrop-filter: blur(8px);
  &:focus-within {
    box-shadow: 0 8px 28px rgba(44, 68, 104, 0.2);
  }
  input {
    flex: 1;
    min-width: 0;
    height: 46px;
    padding: 0 14px;
    border: 0;
    outline: 0;
    background: transparent;
    color: #25324a;
    font-size: 16px;
    &::placeholder {
      color: #9aa4b5;
    }
  }
}
.search-form input::-webkit-search-cancel-button,
.search-form input::-webkit-search-decoration {
  display: none;
  -webkit-appearance: none;
  appearance: none;
}
.search-icon {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
  fill: #9aa4b5;
}
.clear {
  width: 36px;
  height: 40px;
  flex: 0 0 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #70757a;
  font-size: 25px;
  cursor: pointer;
}
.search-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}
.search-button,
.ai-search-button {
  height: 46px;
  padding: 0 22px;
  border: 0;
  border-radius: 24px;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}
.search-button {
  background: #4286f5;
  &:hover {
    background: #3277e8;
  }
}
.ai-search-button {
  background: #18a66a;
  &:hover {
    background: #108c58;
  }
}
.quick-models {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 13px;
  color: #7a8494;
  font-size: 12px;
  span {
    margin-right: 2px;
  }
  button {
    max-width: 100%;
    padding: 5px 10px;
    overflow: hidden;
    border: 1px solid rgba(199, 140, 34, 0.3);
    border-radius: 15px;
    background: rgba(255, 255, 255, 0.7);
    color: #596579;
    font-size: 12px;
    line-height: 1.2;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
    backdrop-filter: blur(6px);
    &:hover {
      border-color: #dca536;
      background: #fff8df;
      color: #9a6810;
    }
  }
}
.mid-autumn-theme {
  .search-group::before,
  .search-group::after {
    position: absolute;
    z-index: 2;
    pointer-events: none;
    color: #e7a62a;
    filter: drop-shadow(0 2px 3px rgba(189, 126, 18, 0.2));
    animation: festivalPetal 3.2s ease-in-out infinite;
  }
  .search-group::before {
    content: "❀";
    left: -9px;
    bottom: 39px;
    font-size: 24px;
    transform: rotate(-18deg);
  }
  .search-group::after {
    content: "✦";
    right: -7px;
    bottom: 43px;
    color: #f0bd46;
    font-size: 20px;
    animation-delay: -1.4s;
  }
  .search-form {
    border-color: rgba(221, 166, 57, 0.72);
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.98),
      rgba(255, 250, 233, 0.98),
      rgba(255, 255, 255, 0.98)
    );
    box-shadow:
      0 7px 24px rgba(173, 112, 23, 0.18),
      0 0 0 3px rgba(255, 222, 139, 0.16);
  }
  .search-form::before {
    content: "";
    position: absolute;
    inset: -3px;
    z-index: -1;
    border-radius: 33px;
    background: linear-gradient(
      100deg,
      transparent 15%,
      rgba(255, 205, 91, 0.48) 42%,
      transparent 68%
    );
    background-size: 220% 100%;
    animation: festivalShimmer 3.8s linear infinite;
  }
  .search-icon {
    fill: #c78c22;
  }
  .search-button {
    padding-left: 35px;
    background: linear-gradient(135deg, #e8a927, #f4c957);
    box-shadow: 0 4px 12px rgba(196, 129, 22, 0.24);
    &::before {
      content: "❀";
      margin-right: 7px;
      font-size: 14px;
    }
    &:hover {
      background: linear-gradient(135deg, #d99a19, #edbd43);
    }
  }
  .ai-search-button {
    padding-left: 34px;
    background: linear-gradient(135deg, #173d83, #315dab);
    box-shadow: 0 4px 12px rgba(31, 68, 132, 0.24);
    &::before {
      content: "☾";
      margin-right: 7px;
      color: #ffe4a0;
      font-size: 16px;
    }
    &:hover {
      background: linear-gradient(135deg, #123472, #284f98);
    }
  }
}
@keyframes festivalPetal {
  0%,
  100% {
    transform: translateY(0) rotate(-18deg);
    opacity: 0.72;
  }
  50% {
    transform: translateY(-7px) rotate(9deg);
    opacity: 1;
  }
}
@keyframes festivalShimmer {
  from {
    background-position: 190% 0;
  }
  to {
    background-position: -30% 0;
  }
}
.national-day-theme {
  .search-group::before,
  .search-group::after {
    position: absolute;
    z-index: 2;
    pointer-events: none;
    color: #e12a2f;
    filter: drop-shadow(0 2px 3px rgba(164, 25, 25, 0.2));
    animation: nationalStar 2.6s ease-in-out infinite;
  }
  .search-group::before {
    content: "★";
    left: -8px;
    bottom: 40px;
    font-size: 22px;
  }
  .search-group::after {
    content: "✦";
    right: -7px;
    bottom: 43px;
    color: #f1bb38;
    font-size: 20px;
    animation-delay: -1.1s;
  }
  .search-form {
    border-color: rgba(207, 36, 40, 0.68);
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.98),
      rgba(255, 247, 229, 0.98),
      rgba(255, 255, 255, 0.98)
    );
    box-shadow:
      0 7px 24px rgba(159, 31, 30, 0.18),
      0 0 0 3px rgba(245, 191, 55, 0.14);
  }
  .search-form::before {
    content: "";
    position: absolute;
    inset: -3px;
    z-index: -1;
    border-radius: 33px;
    background: linear-gradient(
      100deg,
      transparent 12%,
      rgba(229, 36, 41, 0.22) 35%,
      rgba(250, 194, 54, 0.52) 48%,
      transparent 70%
    );
    background-size: 230% 100%;
    animation: nationalShimmer 3.4s linear infinite;
  }
  .search-icon {
    fill: #cc2428;
  }
  .search-button {
    padding-left: 35px;
    background: linear-gradient(135deg, #d51f27, #ef4a37);
    box-shadow: 0 4px 12px rgba(181, 34, 34, 0.25);
    &::before {
      content: "★";
      margin-right: 7px;
      color: #ffd76a;
      font-size: 13px;
    }
    &:hover {
      background: linear-gradient(135deg, #bd161d, #df392b);
    }
  }
  .ai-search-button {
    padding-left: 34px;
    background: linear-gradient(135deg, #8d1519, #bd2428);
    box-shadow: 0 4px 12px rgba(126, 23, 23, 0.25);
    &::before {
      content: "✦";
      margin-right: 7px;
      color: #ffe09a;
      font-size: 15px;
    }
    &:hover {
      background: linear-gradient(135deg, #761014, #a91c21);
    }
  }
}
@keyframes nationalStar {
  0%,
  100% {
    transform: scale(0.88) rotate(-8deg);
    opacity: 0.68;
  }
  50% {
    transform: scale(1.12) rotate(8deg);
    opacity: 1;
  }
}
@keyframes nationalShimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -30% 0;
  }
}
footer {
  position: relative;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: end;
  gap: 20px;
  min-height: 54px;
  padding: 0 28px 12px;
  box-sizing: border-box;
  background: transparent;
  color: #70757a;
  font-size: 14px;
  a {
    color: #70757a;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
}
.footer-info-links {
  justify-self: start;
  display: flex;
  gap: 16px;
  white-space: nowrap;
}
.footer-copyright {
  justify-self: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  white-space: nowrap;
}
.footer-links {
  justify-self: end;
  display: flex;
  gap: 26px;
}
.festival-greeting-mask {
  position: fixed;
  z-index: 4000;
  inset: 0;
  display: grid;
  place-items: center;
  padding: 18px;
  box-sizing: border-box;
  background: rgba(20, 26, 36, 0.26);
  backdrop-filter: blur(3px);
}
.festival-greeting-card {
  position: relative;
  width: 50vw;
  max-width: 760px;
  max-height: calc(100vh - 36px);
  min-height: clamp(220px, 28vw, 400px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: clamp(20px, 3vw, 48px);
  box-sizing: border-box;
  border: 1px solid rgba(255, 224, 159, 0.92);
  border-radius: clamp(18px, 2.2vw, 32px);
  text-align: center;
  box-shadow: 0 28px 80px rgba(55, 26, 10, 0.28);
}
.festival-greeting-card::before,
.festival-greeting-card::after {
  content: "";
  position: absolute;
  pointer-events: none;
  border-radius: 50%;
}
.festival-greeting-card::before {
  width: 55%;
  aspect-ratio: 1;
  top: -35%;
  right: -15%;
  opacity: 0.34;
}
.festival-greeting-card::after {
  width: 42%;
  aspect-ratio: 1;
  left: -18%;
  bottom: -30%;
  opacity: 0.28;
}
.mid-autumn-greeting {
  background: radial-gradient(
    circle at 50% 18%,
    rgba(255, 250, 217, 0.98),
    rgba(255, 242, 190, 0.97) 45%,
    rgba(238, 178, 72, 0.98)
  );
  color: #704312;
}
.mid-autumn-greeting::before {
  background: radial-gradient(
    circle,
    #fffbdc 0 33%,
    rgba(255, 226, 127, 0.72) 34% 47%,
    transparent 49%
  );
  box-shadow: 0 0 45px rgba(255, 230, 148, 0.75);
}
.mid-autumn-greeting::after {
  background: radial-gradient(circle, rgba(244, 179, 44, 0.7), transparent 68%);
}
.national-day-greeting {
  background: radial-gradient(
    circle at 50% 15%,
    #fff5c8 0,
    #e83b30 52%,
    #a80713 100%
  );
  color: #fff7d4;
  border-color: #ffd35d;
}
.national-day-greeting::before {
  background: radial-gradient(
    circle,
    #ffd95c,
    rgba(255, 181, 33, 0.48) 40%,
    transparent 70%
  );
}
.national-day-greeting::after {
  background: radial-gradient(circle, rgba(255, 212, 75, 0.8), transparent 68%);
}
.festival-greeting-close {
  position: absolute;
  z-index: 3;
  top: 14px;
  right: 16px;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: currentColor;
  font-size: 25px;
  line-height: 30px;
  cursor: pointer;
}
.greeting-art {
  position: relative;
  z-index: 1;
  width: min(72%, 460px);
  margin-bottom: clamp(10px, 2vw, 22px);
}
.greeting-art img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 8px 15px rgba(84, 37, 7, 0.16));
}
.greeting-copy {
  position: relative;
  z-index: 1;
}
.greeting-kicker {
  margin: 0 0 8px;
  font-size: clamp(12px, 1.2vw, 17px);
  font-weight: 700;
  letter-spacing: 0.22em;
}
.greeting-copy h2 {
  margin: 0;
  font-size: clamp(26px, 3.2vw, 48px);
  line-height: 1.25;
  letter-spacing: 0.06em;
}
.greeting-subtitle {
  margin-top: 9px !important;
}
.greeting-spark {
  position: absolute;
  z-index: 1;
  color: #ffe079;
  text-shadow: 0 0 10px rgba(255, 211, 78, 0.65);
  animation: greetingSpark 1.5s ease-in-out infinite;
}
.spark-one {
  top: 17%;
  left: 10%;
  font-size: 24px;
}
.spark-two {
  top: 28%;
  right: 10%;
  font-size: 17px;
  animation-delay: -0.5s;
}
.spark-three {
  right: 17%;
  bottom: 17%;
  font-size: 9px;
  animation-delay: -1s;
}
.greeting-progress {
  position: absolute;
  z-index: 2;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 5px;
  transform-origin: left center;
  background: linear-gradient(90deg, #ffd55d, #fff3b7);
  animation: greetingCountdown 3s linear forwards;
}
.festival-greeting-enter-active,
.festival-greeting-leave-active {
  transition: opacity 0.28s ease;
}
.festival-greeting-enter-active .festival-greeting-card,
.festival-greeting-leave-active .festival-greeting-card {
  transition:
    transform 0.28s ease,
    opacity 0.28s ease;
}
.festival-greeting-enter,
.festival-greeting-leave-to {
  opacity: 0;
}
.festival-greeting-enter .festival-greeting-card,
.festival-greeting-leave-to .festival-greeting-card {
  opacity: 0;
  transform: translateY(18px) scale(0.96);
}
@keyframes greetingSpark {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.8) rotate(0);
  }
  50% {
    opacity: 1;
    transform: scale(1.2) rotate(20deg);
  }
}
@keyframes greetingCountdown {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
@media (max-width: 1200px) {
  .footer-info-links {
    gap: 8px;
    font-size: 11px;
  }
  .footer-copyright {
    font-size: 10px;
    gap: 2px;
  }
  .footer-links {
    gap: 10px;
    font-size: 11px;
  }
}
@media (max-width: 700px) {
  .search-home {
    grid-template-rows: 58px 1fr auto;
    background-position: 42% center;
  }
  .topbar {
    padding: 6px 8px;
  }
  .primary-nav {
    gap: 0;
    a {
      padding: 8px 3px;
      font-size: 11px;
    }
  }
  .top-actions {
    gap: 2px;
  }
  .language-button {
    width: 32px;
    height: 32px;
  }
  .language-button svg {
    width: 19px;
    height: 19px;
  }
  .login {
    display: block;
    min-width: 48px;
    height: 32px;
    padding: 0 10px;
    border-radius: 6px;
    font-size: 12px;
  }
  .user-entry {
    width: 28px;
    height: 28px;
    padding: 3px;
    background: transparent;
    box-shadow: none;
  }
  .user-entry img {
    width: 20px;
    height: 20px;
  }
  .user-entry span {
    display: none;
  }
  .search-stage {
    padding: 10px 14px 10vh;
  }
  .search-group {
    width: calc(100vw - 28px);
  }
  .brand-logo {
    margin-bottom: 28px;
  }
  .anniversary-badge {
    margin: -18px 0 15px;
    padding: 5px 12px;
    font-size: 11px;
    letter-spacing: 1px;
  }
  .search-form {
    height: 54px;
    padding-left: 14px;
  }
  .search-form input {
    height: 42px;
    padding: 0 8px;
    font-size: 14px;
  }
  .search-icon {
    display: none;
  }
  .search-actions {
    gap: 4px;
  }
  .search-button,
  .ai-search-button {
    height: 42px;
    padding: 0 12px;
    font-size: 12px;
  }
  .quick-models {
    gap: 5px;
    margin-top: 10px;
    font-size: 10px;
  }
  .quick-models button {
    padding: 4px 7px;
    font-size: 10px;
  }
  .quick-models button:nth-of-type(4) {
    display: none;
  }
  .mid-autumn-theme .search-button,
  .mid-autumn-theme .ai-search-button,
  .national-day-theme .search-button,
  .national-day-theme .ai-search-button {
    padding: 0 10px;
  }
  .mid-autumn-theme .search-button::before,
  .mid-autumn-theme .ai-search-button::before,
  .national-day-theme .search-button::before,
  .national-day-theme .ai-search-button::before {
    margin-right: 3px;
  }
  .mid-autumn-theme .search-group::before,
  .national-day-theme .search-group::before {
    left: -3px;
    font-size: 17px;
  }
  .mid-autumn-theme .search-group::after,
  .national-day-theme .search-group::after {
    right: -2px;
    font-size: 15px;
  }
  .festival-greeting-card {
    width: 50vw;
    min-height: 230px;
    padding: 24px 10px;
  }
  .greeting-art {
    width: 92%;
    margin-bottom: 8px;
  }
  .greeting-kicker {
    margin-bottom: 5px;
    font-size: 9px;
    letter-spacing: 0.08em;
  }
  .greeting-copy h2 {
    font-size: 18px;
    letter-spacing: 0.02em;
  }
  .greeting-subtitle {
    margin-top: 6px !important;
  }
  .festival-greeting-close {
    top: 7px;
    right: 7px;
    width: 26px;
    height: 26px;
    font-size: 19px;
    line-height: 22px;
  }
  footer {
    display: flex;
    min-height: 58px;
    align-items: center;
    justify-content: center;
    padding: 0 10px 7px;
    font-size: 10px;
  }
  .footer-info-links,
  .footer-links {
    display: none;
  }
  .footer-copyright {
    flex-wrap: wrap;
    justify-content: center;
    gap: 1px 3px;
    line-height: 14px;
    text-align: center;
    white-space: normal;
  }
}
</style>
